import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Upload, AlertCircle, FileText, CheckCircle, Database } from 'lucide-react';
import * as XLSX from 'xlsx';
import { usePreventScroll } from '../../utils/hooks';
import { formatNumber } from '../../utils/calculations';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';
import Modal from '../Layout/Modal.jsx';
import { materialsAPI } from '../../utils/api';
import '../Recipes/RecipeForm.css';

const ExcelUpload = ({ categories, onUpload, onCancel, isUploading = false }) => {
  const { t, i18n } = useTranslation();
  const [uploadedData, setUploadedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [showAllergenErrorModal, setShowAllergenErrorModal] = useState(false);
  const [allergenErrorMessage, setAllergenErrorMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const modalContentRef = useRef(null);
  const fileInputRef = useRef(null);

  // Prevent background scrolling when modal is open
  usePreventScroll(isModalOpen);

  // Handle clicks outside modal content
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        // Clicked outside modal content - prevent default behavior
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleScroll = (event) => {
      // Only allow scrolling within the modal content
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, { passive: false });

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleOverlayClick = (event) => {
    // Only prevent closing when clicking outside modal content
    if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    
    if (files.length === 0) {
      setError('No files were dropped');
      return;
    }

    const excelFile = files.find(file => {
      const fileName = file.name.toLowerCase();
      const fileType = file.type.toLowerCase();
      
      return (
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileType === 'application/vnd.ms-excel' ||
        fileName.endsWith('.xlsx') ||
        fileName.endsWith('.xls')
      );
    });

    if (excelFile) {
      handleFileUpload({ target: { files: [excelFile] } });
    } else {
      setError('Please import a valid Excel file (.xlsx or .xls). The file you dropped is not a supported Excel format.');
    }
  };

  const downloadTemplate = () => {
    // Combine template and sample data into one sheet
    const templateData = [
      {
        'Material Name (EN)': 'Tomato',
        'Material Name (AR)': 'طماطم',
        'Brand Name': 'Fresh Market',
        'Main Unit': 'carton',
        'Quantity per Main Unit': '24',
        'Quantity per Piece': '400',
        'Sub Unit': 'g',
        'Price with VAT': '48.00',
        'Price without VAT': '41.74',
        'Supplier': 'ABC Supplier',
        'Category Name': 'Vegetables',
        'Calories': '18.5',
        'Serving Size': '100g',
        'Allergens': ''
      },
      {
        'Material Name (EN)': 'Onion',
        'Material Name (AR)': 'بصل',
        'Brand Name': 'Fresh Market',
        'Main Unit': 'bag',
        'Quantity per Main Unit': '10',
        'Quantity per Piece': '200',
        'Sub Unit': 'g',
        'Price with VAT': '15.00',
        'Price without VAT': '13.04',
        'Supplier': 'ABC Supplier',
        'Category Name': 'Vegetables',
        'Calories': '40.25',
        'Serving Size': '150g',
        'Allergens': ''
      },
      {
        'Material Name (EN)': 'Salt',
        'Material Name (AR)': 'ملح',
        'Brand Name': 'Generic',
        'Main Unit': 'pack',
        'Quantity per Main Unit': '1',
        'Quantity per Piece': '500',
        'Sub Unit': 'g',
        'Price with VAT': '2.00',
        'Price without VAT': '1.74',
        'Supplier': 'Local Store',
        'Category Name': '',
        'Calories': '0',
        'Serving Size': '1g',
        'Allergens': ''
      },
      {
        'Material Name (EN)': 'Wheat Flour',
        'Material Name (AR)': 'دقيق القمح',
        'Brand Name': 'Premium',
        'Main Unit': 'bag',
        'Quantity per Main Unit': '1',
        'Quantity per Piece': '1000',
        'Sub Unit': 'g',
        'Price with VAT': '25.00',
        'Price without VAT': '21.74',
        'Supplier': 'Bakery Supply',
        'Category Name': 'Grains',
        'Calories': '364',
        'Serving Size': '100g',
        'Allergens': 'wheat'
      },
      {
        'Material Name (EN)': 'Mixed Nuts',
        'Material Name (AR)': 'مكسرات مختلطة',
        'Brand Name': 'Organic',
        'Main Unit': 'pack',
        'Quantity per Main Unit': '1',
        'Quantity per Piece': '200',
        'Sub Unit': 'g',
        'Price with VAT': '45.00',
        'Price without VAT': '39.13',
        'Supplier': 'Health Store',
        'Category Name': 'Nuts',
        'Calories': '607',
        'Serving Size': '30g',
        'Allergens': 'tree_nuts, peanuts'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Materials Template');
    
    XLSX.writeFile(wb, 'materials_template.xlsx');
  };

  const exportMaterials = async () => {
    setIsExporting(true);
    try {
      // Fetch materials, categories, and dish usage for export
      const [materials, categoriesData, usedMaterialIds] = await Promise.all([
        materialsAPI.getAll(),
        materialsAPI.getCategories(),
        materialsAPI.getRecipeUsageIds()
      ]);
      const usedIdSet = new Set(usedMaterialIds);
      
      // Transform materials data to match Excel template format
      const exportData = materials.map(material => {
        // Get category name - try multiple field names
        const category = categoriesData.find(cat => cat.id === material.category_id);
        const categoryName = category ? (category.name_en || category.name) : 'Unnamed';
        
        // Format allergens as comma-separated string
        const allergensString = material.allergens && material.allergens.length > 0 
          ? material.allergens.join(', ') 
          : '';
        
        return {
          'Material Name (EN)': material.name_en || '',
          'Material Name (AR)': material.name_ar || '',
          'Brand Name': material.brand_name || '',
          'Main Unit': material.main_unit || '',
          'Quantity per Main Unit': material.quantity_per_main_unit || '',
          'Quantity per Piece': material.quantity_per_piece || '',
          'Sub Unit': material.sub_unit || '',
          'Price with VAT': material.price_with_vat || '',
          'Price without VAT': material.price_without_vat || '',
          'Supplier': material.supplier || '',
          'Category Name': categoryName,
          'Calories': material.calories || '',
          'Serving Size': material.serving_size || '',
          'Allergens': allergensString,
          'Used in Dish': usedIdSet.has(material.id) ? 'Yes' : 'No'
        };
      });
      
      // Create Excel file
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Materials Export');
      
      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `materials_export_${dateStr}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      
    } catch (error) {
      console.error('Error exporting materials:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`Failed to export materials: ${errorMessage}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    setError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setError('No data found in the Excel file');
          setIsProcessing(false);
          return;
        }

        // Check for required columns
        const requiredColumns = ['Material Name (EN)', 'Material Name (AR)'];
        const firstRow = jsonData[0];
        const missingColumns = requiredColumns.filter(col => !firstRow.hasOwnProperty(col));
        
        if (missingColumns.length > 0) {
          setError(`Missing required columns: ${missingColumns.join(', ')}. Please use the provided template.`);
          setIsProcessing(false);
          return;
        }

        // Validate allergen values if Allergens column is present
        const hasAllergensColumn = firstRow.hasOwnProperty('Allergens');
        if (hasAllergensColumn) {
          const validAllergenIds = ['wheat', 'fish', 'peanuts', 'tree_nuts', 'milk', 'celery', 'crustaceans', 'eggs', 'mustard', 'sesame', 'mollusks', 'lupin', 'soybeans', 'sulfites'];
          
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            const allergensString = row['Allergens'];
            if (allergensString && typeof allergensString === 'string') {
              const allergens = allergensString.split(',')
                .map(allergen => allergen.trim().toLowerCase())
                .filter(allergen => allergen.length > 0);
              
              const invalidAllergens = allergens.filter(allergen => !validAllergenIds.includes(allergen));
              if (invalidAllergens.length > 0) {
                setAllergenErrorMessage(`Invalid allergen values in row ${i + 1}: ${invalidAllergens.join(', ')}\n\nValid values are: ${validAllergenIds.join(', ')}`);
                setShowAllergenErrorModal(true);
                setIsProcessing(false);
                return;
              }
            }
          }
        }
        
        // Check for VAT pricing columns
        const hasPriceWithVat = firstRow.hasOwnProperty('Price with VAT');
        const hasPriceWithoutVat = firstRow.hasOwnProperty('Price without VAT');
        
        if (!hasPriceWithVat && !hasPriceWithoutVat) {
          setError('Missing VAT pricing columns. Please use the provided template with "Price with VAT" and/or "Price without VAT" columns.');
          setIsProcessing(false);
          return;
        }
        
        // Validate VAT calculations if both columns are present
        if (hasPriceWithVat && hasPriceWithoutVat) {
          const vatPercentage = 15.00; // Default VAT
          let hasVatMismatch = false;
          
          for (const row of jsonData) {
            const priceWithVat = parseFloat(row['Price with VAT']) || 0;
            const priceWithoutVat = parseFloat(row['Price without VAT']) || 0;
            
            if (priceWithVat > 0 && priceWithoutVat > 0) {
              const expectedPriceWithoutVat = priceWithVat / (1 + (vatPercentage / 100));
              const tolerance = 0.01; // 1 cent tolerance
              
              if (Math.abs(expectedPriceWithoutVat - priceWithoutVat) > tolerance) {
                hasVatMismatch = true;
                break;
              }
            }
          }
          
          if (hasVatMismatch) {
            console.log('VAT calculation mismatch detected - will correct automatically');
          }
        }

        console.log('Raw Excel data:', jsonData); // Debug log
        const processedData = processExcelData(jsonData);
        console.log('Processed data:', processedData); // Debug log
        setUploadedData(processedData);
        setIsProcessing(false);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        setError('Error processing Excel file: ' + error.message);
        setIsProcessing(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Helper function to safely convert Excel values to strings
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const processExcelData = (jsonData) => {
    const vatPercentage = 15.00; // Default VAT percentage
    
    return jsonData.map((row, index) => {
      // Store category name from Excel, but don't create category yet
      let categoryName = '';
      let categoryId = '';
      
      const categoryValue = safeString(row['Category Name']);
      if (categoryValue) {
        categoryName = categoryValue;
        
        // Check if category already exists - try multiple field names and normalize
        const existingCategory = categories.find(cat => {
          const nameEn = cat.name_en || cat.name;
          if (!nameEn) return false;
          
          // Normalize both strings for comparison
          const normalizedCategoryName = categoryName.toLowerCase().trim();
          const normalizedCatName = nameEn.toLowerCase().trim();
          
          return normalizedCatName === normalizedCategoryName;
        });
        
        
        if (existingCategory) {
          categoryId = existingCategory.id;
          categoryName = ''; // Clear categoryName since we found existing category
        } else if (categoryName.toLowerCase() === 'unnamed' || categoryName.toLowerCase() === 'default') {
          // Special handling for "Unnamed" or "Default" category - use the default category
          const defaultCategory = categories.find(cat => cat.id === 0);
          if (defaultCategory) {
            categoryId = 0;
            categoryName = ''; // Clear categoryName to avoid creating new category
          }
        }
        // If category doesn't exist, we'll create it during save
      } else {
        // If category is empty or missing, assign to "Unnamed" category
        const defaultCategory = categories.find(cat => cat.id === 0);
        if (defaultCategory) {
          categoryId = 0;
          // Don't set categoryName to avoid confusion - just use the existing category
        }
      }

      // Handle VAT pricing calculations
      let priceWithVat = safeString(row['Price with VAT']);
      let priceWithoutVat = safeString(row['Price without VAT']);
      
      // If only one price is provided, calculate the other
      if (priceWithVat && !priceWithoutVat) {
        // User provided price with VAT, calculate price without VAT
        const priceWithVatNum = parseFloat(priceWithVat);
        if (!isNaN(priceWithVatNum)) {
          priceWithoutVat = formatNumber(priceWithVatNum / (1 + (vatPercentage / 100)), 2);
        }
      } else if (priceWithoutVat && !priceWithVat) {
        // User provided price without VAT, calculate price with VAT
        const priceWithoutVatNum = parseFloat(priceWithoutVat);
        if (!isNaN(priceWithoutVatNum)) {
          priceWithVat = formatNumber(priceWithoutVatNum * (1 + (vatPercentage / 100)), 2);
        }
      } else if (priceWithVat && priceWithoutVat) {
        // Both provided - validate and correct if needed
        const priceWithVatNum = parseFloat(priceWithVat);
        const priceWithoutVatNum = parseFloat(priceWithoutVat);
        
        if (!isNaN(priceWithVatNum) && !isNaN(priceWithoutVatNum)) {
          const expectedPriceWithoutVat = priceWithVatNum / (1 + (vatPercentage / 100));
          const tolerance = 0.01;
          
          if (Math.abs(expectedPriceWithoutVat - priceWithoutVatNum) > tolerance) {
            // Correct the price without VAT
            priceWithoutVat = formatNumber(expectedPriceWithoutVat, 2);
            console.log(`VAT calculation corrected for row ${index + 1}: ${row['Material Name (EN)']}`);
          }
        }
      }

      // Process allergens from comma-separated string
      const allergensString = safeString(row['Allergens']);
      let allergens = [];
      if (allergensString) {
        // Split by comma and clean up each allergen
        allergens = allergensString.split(',')
          .map(allergen => allergen.trim().toLowerCase())
          .filter(allergen => allergen.length > 0);
      }

      return {
        nameEn: safeString(row['Material Name (EN)']),
        nameAr: safeString(row['Material Name (AR)']),
        brandName: safeString(row['Brand Name']),
        mainUnit: safeString(row['Main Unit']),
        quantityPerMainUnit: safeString(row['Quantity per Main Unit']),
        quantityPerPiece: safeString(row['Quantity per Piece']),
        subUnit: safeString(row['Sub Unit']),
        priceWithVat: priceWithVat,
        priceWithoutVat: priceWithoutVat,
        supplier: safeString(row['Supplier']),
        calories: safeString(row['Calories']),
        servingSize: safeString(row['Serving Size']),
        allergens: allergens,
        categoryId: categoryId,
        categoryName: categoryName // Store the category name for later use
      };
    }).filter(material => material.nameEn && material.nameAr); // Only include materials with names
  };

  const handleUpload = () => {
    if (uploadedData.length === 0) {
      setError('No valid data to upload');
      return;
    }
    onUpload(uploadedData);
  };

  const handleRemoveFile = () => {
    setUploadedData([]);
    setError('');
    setIsProcessing(false);
    setIsDragOver(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Helper function to get category display name
  const getCategoryDisplayName = (material) => {
    if (material.categoryId) {
      // Category exists, get its name
      const category = categories.find(cat => cat.id === material.categoryId);
      return category ? category.name_en : 'Unknown';
    } else if (material.categoryName) {
      // Category doesn't exist yet, show the name from Excel
      return material.categoryName;
    }
    return 'Unnamed';
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="recipe-form-shell modal-content" ref={modalContentRef}>
        {/* Header with compact spacing */}
        <div className="flex justify-between items-center pt-4 pb-2 px-6">
          <h2 className="text-2xl font-semibold text-card-foreground">
            {t('materials.excelImportExport')}
          </h2>
          <button
            onClick={() => {
              setIsModalOpen(false);
              onCancel();
            }}
            className="text-muted-foreground hover:text-foreground p-2 rounded-md hover:bg-muted transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab Panels Viewport */}
          <div 
            className="tab-panels-viewport"
            style={{ height: '100%' }}
          >
            {/* Excel Import/Export Tab */}
            <div className="tab-panel is-active p-4 flex flex-col">
              <div className="max-w-7xl mx-auto w-full space-y-4">
                {/* Upload Progress Indicator */}
                {isUploading && (
                  <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-4"></div>
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-card-foreground">{t('materials.importingMaterials')}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('materials.importingMaterialsDescription')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Download Template Card - Hidden when preview is shown */}
                {uploadedData.length === 0 && (
                  <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                          <Download className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-card-foreground">{t('materials.downloadTemplate')}</h3>
                          <p className="text-sm text-muted-foreground">{t('materials.downloadTemplateDescription')}</p>
                        </div>
                        </div>
                      <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                        <button
                          onClick={exportMaterials}
                          disabled={isExporting}
                          className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors whitespace-nowrap ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isExporting ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                              {t('materials.exporting')}
                            </>
                          ) : (
                            <>
                              <Database className="h-4 w-4 mr-2" />
                              {t('materials.exportMaterials')}
                            </>
                          )}
                        </button>
                        <button
                          onClick={downloadTemplate}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors whitespace-nowrap"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {t('materials.downloadTemplate')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Allergens Column Information Box */}
                    <div className="bg-muted/50 border border-border rounded-lg p-3">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-card-foreground mb-1">{t('materials.allergensColumn')}</h4>
                          <p className="text-xs text-muted-foreground mb-1">
                            {t('materials.allergensColumnDescription')}
                          </p>
                          <div className="text-xs text-muted-foreground">
                            <strong>{t('materials.validAllergenValues')}</strong> wheat, fish, peanuts, tree_nuts, milk, celery, crustaceans, eggs, mustard, sesame, mollusks, lupin, soybeans, sulfites
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            <strong>{t('materials.allergenExamples')}</strong> <code className="bg-muted px-1 rounded">wheat</code> or <code className="bg-muted px-1 rounded">milk, eggs</code> or <code className="bg-muted px-1 rounded">tree_nuts, peanuts</code>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* File Upload Card - Hidden when preview is shown */}
                {uploadedData.length === 0 && (
                  <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                        <Upload className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-card-foreground">{t('materials.importExcelFile')}</h3>
                        <p className="text-sm text-muted-foreground">{t('materials.importExcelDescription')}</p>
                      </div>
                    </div>
                    
                    <div 
                      className={`border-2 border-dashed rounded-lg p-4 text-center transition-all duration-200 ${
                        isDragOver 
                          ? 'border-primary bg-primary/10 shadow-lg' 
                          : 'border-border hover:border-primary'
                      }`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="space-y-2">
                        <div className={`p-2 rounded-full w-16 h-16 mx-auto flex items-center justify-center transition-colors ${
                          isDragOver ? 'bg-primary/20' : 'bg-muted/50'
                        }`}>
                          <Upload className={`h-6 w-6 transition-colors ${
                            isDragOver ? 'text-primary' : 'text-muted-foreground'
                          }`} />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-base font-medium text-card-foreground">
                            {isDragOver ? t('materials.dropExcelFile') : t('materials.importExcelFile')}
                          </h4>
                          <p className="text-sm text-muted-foreground max-w-md mx-auto">
                            {isDragOver 
                              ? t('materials.releaseToImport') 
                              : t('materials.dragAndDropDescription')
                            }
                          </p>
                          <div className="space-y-1 text-xs text-muted-foreground">
                            <div className="flex items-center justify-center space-x-1">
                              <CheckCircle className="h-3 w-3 text-chart-3" />
                              <span>{t('materials.caloriesWholeNumbers')}</span>
                            </div>
                            <div className="flex items-center justify-center space-x-1">
                              <AlertCircle className="h-3 w-3 text-chart-4" />
                              <span>{t('materials.emptyCategoriesUnnamed')}</span>
                            </div>
                            <div className="flex items-center justify-center space-x-1">
                              <AlertCircle className="h-3 w-3 text-primary" />
                              <span>{t('materials.allergensCommaSeparated')}</span>
                            </div>
                          </div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="file-upload"
                            disabled={isUploading}
                          />
                          <button
                            type="button"
                            className={`inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={(e) => {
                              // Prevent the click from being handled by the modal overlay
                              e.stopPropagation();
                              // Trigger file input click
                              if (fileInputRef.current && !isUploading) {
                                fileInputRef.current.click();
                              }
                            }}
                            disabled={isUploading}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            {isUploading ? t('materials.importInProgress') : t('materials.importFile')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Processing Status */}
                {isProcessing && (
                  <div className="bg-card rounded-xl shadow-sm border border-border p-8">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-4"></div>
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-card-foreground">Processing Excel File</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Please wait while we process your Excel data...
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="bg-card rounded-xl shadow-sm border border-destructive/20 p-8">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-6 w-6 text-destructive" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-destructive mb-2">Upload Error</h3>
                        <p className="text-sm text-destructive/80">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview Uploaded Data */}
                {uploadedData.length > 0 && (
                  <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-card-foreground">
                          {t('materials.preview')} ({uploadedData.length} {t('common.materials')})
                        </h3>
                        <p className="text-sm text-muted-foreground">{t('materials.previewDescription')}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2 text-sm text-chart-3">
                          <CheckCircle className="h-4 w-4" />
                          <span className="font-medium">{t('materials.readyToImport')}</span>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          className="inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                        >
                          <X className="h-4 w-4 mr-1" />
                          {t('materials.removeFile')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Categories Summary */}
                    {(() => {
                      const newCategories = uploadedData
                        .filter(material => !material.categoryId && material.categoryName)
                        .map(material => material.categoryName)
                        .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates
                      
                      const existingCategories = uploadedData
                        .filter(material => material.categoryId)
                        .map(material => {
                          const category = categories.find(cat => cat.id === material.categoryId);
                          return category ? category.name_en : 'Unknown';
                        })
                        .filter((name, index, arr) => arr.indexOf(name) === index); // Remove duplicates
                      
                      const unnamedMaterials = uploadedData
                        .filter(material => material.categoryId === 0)
                        .length;
                      
                      // Check for VAT calculation corrections
                      const vatCorrections = uploadedData.filter(material => {
                        const originalPriceWithoutVat = parseFloat(material.originalPriceWithoutVat || 0);
                        const calculatedPriceWithoutVat = parseFloat(material.priceWithoutVat || 0);
                        return originalPriceWithoutVat > 0 && Math.abs(originalPriceWithoutVat - calculatedPriceWithoutVat) > 0.01;
                      }).length;
                      
                      if (newCategories.length > 0 || existingCategories.length > 0 || unnamedMaterials > 0 || vatCorrections > 0) {
                        return (
                          <div className="bg-muted/50 p-4 rounded-lg space-y-3 mb-4">
                            {newCategories.length > 0 && (
                              <div>
                                <h4 className="font-medium text-primary mb-2 flex items-center text-sm">
                                  <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                                  {t('materials.newCategories')} ({newCategories.length}):
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {newCategories.map((categoryName, index) => (
                                    <span 
                                      key={index}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary"
                                    >
                                      {categoryName}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {existingCategories.length > 0 && (
                              <div>
                                <h4 className="font-medium text-chart-3 mb-2 flex items-center text-sm">
                                  <span className="w-2 h-2 bg-chart-3 rounded-full mr-2"></span>
                                  {t('materials.existingCategories')} ({existingCategories.length}):
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {existingCategories.map((categoryName, index) => (
                                    <span 
                                      key={index}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-chart-3/10 text-chart-3"
                                    >
                                      {categoryName}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {unnamedMaterials > 0 && (
                              <div>
                                <h4 className="font-medium text-chart-4 mb-2 flex items-center text-sm">
                                  <span className="w-2 h-2 bg-chart-4 rounded-full mr-2"></span>
                                  {t('materials.unnamedMaterials')} ({unnamedMaterials}):
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-chart-4/10 text-chart-4">
                                    {t('materials.unnamed')}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            {vatCorrections > 0 && (
                              <div>
                                <h4 className="font-medium text-secondary mb-2 flex items-center text-sm">
                                  <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                                  {t('materials.vatCalculations')} ({vatCorrections}):
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-secondary/10 text-secondary">
                                    {t('materials.vatCalculationMismatch')}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Responsive Table */}
                    <div className="bg-muted/50 rounded-lg border border-border overflow-hidden" style={{ maxHeight: '400px' }}>
                      <div className="overflow-y-auto" style={{ maxHeight: '350px' }}>
                        <table className="w-full border-collapse">
                          <thead className="bg-muted">
                            <tr>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.nameEn')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.nameAr')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.brand')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.mainUnit')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.qtyMain')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.qtyPiece')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.subUnit')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.priceWithVat')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.priceWithoutVat')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.supplier')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.calories')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.serving')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('materials.allergens')}</th>
                              <th className="text-center border border-border bg-muted px-3 py-2 text-sm font-medium text-card-foreground">{t('common.category')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-card">
                            {uploadedData.slice(0, 8).map((material, index) => (
                              <tr key={index} className="hover:bg-muted/50 transition-colors">
                                <td className="font-medium text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.nameEn}</td>
                                <td className="font-medium text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.nameAr}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.brandName || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.mainUnit || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.quantityPerMainUnit || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.quantityPerPiece || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.subUnit || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.priceWithVat || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.priceWithoutVat || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.supplier || '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.calories ? Math.round(material.calories) : '-'}</td>
                                <td className="text-sm text-center border border-border px-3 py-2 text-card-foreground">{material.servingSize || '-'}</td>
                                <td className="text-center border border-border px-3 py-2">
                                  {material.allergens && material.allergens.length > 0 ? (
                                    <div className="flex justify-center">
                                      <AllergenBadge 
                                        allergens={material.allergens}
                                        variant="warning"
                                        size="xs"
                                        maxDisplay={2}
                                        showCount={true}
                                      />
                                    </div>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">-</span>
                                  )}
                                </td>
                                <td className="text-center border border-border px-3 py-2">
                                  <div className="flex items-center justify-center space-x-2">
                                    <span className="text-sm text-card-foreground">{getCategoryDisplayName(material)}</span>
                                    {!material.categoryId && material.categoryName && (
                                      <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full font-medium">new</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {uploadedData.length > 8 && (
                        <div className="px-4 py-3 bg-muted/50 border-t border-border">
                          <p className="text-sm text-muted-foreground text-center">
                            Showing first 8 of {uploadedData.length} materials
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}


              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions px-6 pb-4">
            <div className={`flex justify-end ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-2'}`}>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  onCancel();
                }}
                className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                disabled={isUploading}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleUpload}
                disabled={uploadedData.length === 0 || isUploading}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${uploadedData.length === 0 || isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isUploading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                    {t('materials.importingMaterials')}...
                  </div>
                ) : (
                  `${t('common.save')} (${uploadedData.length})`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Allergen Error Modal */}
      <Modal isOpen={showAllergenErrorModal} onClose={() => setShowAllergenErrorModal(false)}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">Invalid Allergen Values</h2>
        </div>
        <div className="mb-8">
          <div className="text-card-foreground whitespace-pre-line">
            {allergenErrorMessage}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => {
              setShowAllergenErrorModal(false);
              // Clear the file input so the same file can be uploaded again
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            className="btn btn-primary"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ExcelUpload; 